/**
 * The doctors list's URL state and the POST /admin/doctors-list body built
 * from it. Every key is text, as the URL holds it (hooks/useListParams).
 * drcfo.backend rejects a value it does not know, so a hand-edited URL value
 * is dropped here instead of failing the whole list.
 */

export const PAGE_SIZES = [10, 25, 50, 100];

export const DOCTOR_LIST_DEFAULTS = {
  tab: "active", // active | inactive
  page: "1",
  size: "25",
  q: "",
  plan: "", // subscription plan id
  sub: "", // current | expired
  hfr: "", // has | none | verified | unchecked | failing
  hip: "", // has | none | verified | unchecked | failing
  hiu: "", // has | none | verified | unchecked | failing
  from: "", // joined on or after, YYYY-MM-DD
  to: "", // joined on or before, YYYY-MM-DD
  sort: "newest", // newest | oldest
};

/** The filters "Clear filters" resets (the tab and page size stay). */
export const DOCTOR_LIST_FILTER_KEYS = [
  "q",
  "plan",
  "sub",
  "hfr",
  "hip",
  "hiu",
  "from",
  "to",
  "sort",
];

export const SUBSCRIPTION_OPTIONS = [
  { value: "", label: "Any" },
  { value: "current", label: "Not expired" },
  { value: "expired", label: "Expired" },
];

/** Whether the profile holds the id at all. Every facility id has these. */
const ID_OPTIONS = [
  { value: "", label: "Any" },
  { value: "has", label: "Has ID" },
  { value: "none", label: "No ID" },
];

/**
 * What the ABDM §3.2.6 check said about the id now on the profile, as its
 * badge shows it (utils/abdmServiceStatus.js): each id has a status of its
 * own, filed by "Verify ABDM IDs" on the profile page. "Not verified" is a
 * present id with no verdict about it: never checked, no answer, or a verdict
 * about an older id.
 */
const CHECK_OPTIONS = [
  { value: "verified", label: "Verified" },
  { value: "unchecked", label: "Not verified" },
  { value: "failing", label: "Failed check" },
];

export const HFR_OPTIONS = [...ID_OPTIONS, ...CHECK_OPTIONS];
export const HIP_OPTIONS = HFR_OPTIONS;
// A HIU ID never checked in its own right is still judged by the HIP ID's
// registration when it is the same Service ID (its `isHiu` flag).
export const HIU_OPTIONS = HFR_OPTIONS;

export const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
];

const known = (options, value) =>
  options.some((option) => option.value && option.value === value);

const PLAN_ID = /^[0-9a-f]{24}$/i;

/** A local calendar day ("YYYY-MM-DD") at its first or last millisecond, or null. */
const localDay = (text, endOfDay) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text || "");
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number);
  const date = endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59, 999)
    : new Date(year, month - 1, day);
  // Rejects 2026-02-31, which Date would roll into March.
  return date.getMonth() === month - 1 && date.getDate() === day ? date : null;
};

/** The filters that hold a value the backend accepts, as text. */
export const validFilters = (params) => ({
  plan: PLAN_ID.test(params.plan || "") ? params.plan : "",
  sub: known(SUBSCRIPTION_OPTIONS, params.sub) ? params.sub : "",
  hfr: known(HFR_OPTIONS, params.hfr) ? params.hfr : "",
  hip: known(HIP_OPTIONS, params.hip) ? params.hip : "",
  hiu: known(HIU_OPTIONS, params.hiu) ? params.hiu : "",
  from: localDay(params.from, false) ? params.from : "",
  to: localDay(params.to, true) ? params.to : "",
  sort: known(SORT_OPTIONS, params.sort) ? params.sort : DOCTOR_LIST_DEFAULTS.sort,
});

/** How many filters (search included) narrow the list now. */
export const activeFilterCount = (params) => {
  const filters = validFilters(params);
  return [
    params.q?.trim(),
    filters.plan,
    filters.sub,
    filters.hfr,
    filters.hip,
    filters.hiu,
    filters.from,
    filters.to,
  ].filter(Boolean).length;
};

/**
 * The POST /admin/doctors-list body for a page. Joining dates are whole local
 * days: `from` at 00:00, `to` at 23:59:59.999, sent as instants.
 */
export const doctorListPayload = ({ params, page, pageSize }) => {
  const filters = validFilters(params);
  const search = params.q?.trim();
  return {
    limit: pageSize,
    page,
    sort: filters.sort,
    ...(search && { search }),
    ...(filters.plan && { plan: filters.plan }),
    ...(filters.sub && { subscription: filters.sub }),
    ...(filters.hfr && { hfr: filters.hfr }),
    ...(filters.hip && { hip: filters.hip }),
    ...(filters.hiu && { hiu: filters.hiu }),
    ...(filters.from && { joinedFrom: localDay(filters.from, false).toISOString() }),
    ...(filters.to && { joinedTo: localDay(filters.to, true).toISOString() }),
  };
};
